import { Component, ElementRef, Inject, Injector, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AbstractComponent, AlertModalComponent, ConfirmationComponent, FirebaseService, Importation, ImportationService, ImportationTypeEnum, UploadTypeEnum, User, UserCardComponent, UtilHelper } from 'lib-trend-core';
import { MatPaginator } from '@angular/material/paginator';
import { MatStepper } from '@angular/material/stepper';

import * as Papa from 'papaparse';
import { finalize, takeUntil } from 'rxjs';

type UserField = 'name' | 'email' | 'phone' | 'roles' | 'ignore';

interface MappingRow {
  csvColumn: string;
  sample: string;
  mappedTo: UserField;
}

@Component({
    selector: 'app-user-upload',
    templateUrl: './user-upload.component.html',
    styleUrl: './user-upload.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UserUploadComponent extends AbstractComponent {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('inputUploadFile', { static: false }) inputFile: ElementRef<HTMLInputElement>;
  public file: File | undefined;
  public fileLoaded = false;
  public url = '';
  public rawCsvText: string = undefined;

  public rawRows: Record<string, any>[] = [];
  public rawHeaders: string[] = [];
  public mappings: MappingRow[] = [];

  public mapColumns: Map<string, string> = new Map();
  public stepIndex = signal(0);

  public csvContent: any[] = [];
  public paginatedContent: any[] = [];
  public pageSize = 5;
  public currentPage = 0;
  public showOnlyErrors = false;

  public availableFields: { value: UserField; label: string }[] = [
    { value: 'name', label: 'user.upload.fieldName' },
    { value: 'email', label: 'user.upload.fieldEmail' },
    { value: 'phone', label: 'user.upload.fieldPhone' },
    { value: 'roles', label: 'user.upload.fieldRoles' },
    { value: 'ignore', label: 'user.upload.fieldIgnore' },
  ];

  constructor(
    injector: Injector,
    public dialogRef: MatDialogRef<UserCardComponent>,
    public dialog: MatDialog,
    private firebaseService: FirebaseService,
    private importationService: ImportationService,
    @Inject(MAT_DIALOG_DATA) public data: User
  ) {
    super(injector)
  }

  handleSelectFile(event: Event): void {
    this.file = (event.target as HTMLInputElement).files?.[0];
    if (!this.file) {
      this.fileLoaded = false;
      return;
    };

    const fileTypeLimit = UtilHelper.getFileTypeLimit(this.file.type);
    if (fileTypeLimit && this.file.size > fileTypeLimit.maxSize) {
      this.openModalAlert(fileTypeLimit.alertTitle, fileTypeLimit.alertMessage);
      return;
    }

    this.fileLoaded = true;

    try {
      this.loading = true
      Promise.all([
        this.file.text(),
        this.firebaseService.uploadFile(this.file, `${this.file?.name}_${Date.now()}`, UploadTypeEnum.CSV)])
        .then(([csvText, { url }]) => {
          this.rawCsvText = csvText
          this.url = url;
        })
        .catch(() => this.alertService.error(this.t('user.upload.errorProcessingFile')))
        .finally(() => (this.loading = false));
    } catch {
      this.alertService.error(this.t('user.upload.errorProcessingFile'));
      this.loading = false;
    }
  }

  clearFile(): void {
    this.file = undefined;
    this.fileLoaded = false;
    this.url = '';
    this.rawCsvText = undefined;
    this.rawRows = [];
    this.rawHeaders = [];
    this.mappings = [];
    this.csvContent = [];
    if (this.inputFile?.nativeElement) {
      this.inputFile.nativeElement.value = '';
    }
  }

  parseCsvAndStoreRaw(): void {
    if (!this.rawCsvText || typeof this.rawCsvText !== 'string') {
      this.openModalAlert(this.t('user.upload.missingFileTitle'), this.t('user.upload.missingFileMessage'));
      return;
    }

    Papa.parse(this.rawCsvText, {
      header: true,
      skipEmptyLines: true,
      complete: (results: Papa.ParseResult<any>) => {
        const rows = (results.data as any[]).filter(
          r => !!r && Object.keys(r).length > 1,
        );
        this.rawRows = rows;
        this.rawHeaders = results.meta.fields || Object.keys(rows[0] || {});
        this.buildDefaultMappings();
      },
      error: () => {
        this.alertService.error(this.t('user.upload.errorProcessingCsv'));
      },
    });
  }

  next(): void {
    if (this.canGoNext()) this.stepIndex.update(i => i + 1);
  }

  prev(): void {
    if (this.stepIndex() > 0) this.stepIndex.update(i => i - 1);
  }

  canGoNext(): boolean {
    if (this.stepIndex() === 0) return !!this.fileLoaded && !this.loading;
    if (this.stepIndex() === 1) return this.isMappingValid();
    return true;
  }

  save(): void {
    const errorCount = this.getErrorCount();
    const validCount = this.csvContent.length - errorCount;

    if (validCount === 0) {
      this.alertService.error(this.t('user.upload.noValidUser'));
      return;
    }

    const mappings = Object.fromEntries(
      this.mappings
        .filter(m => m.mappedTo !== 'ignore')
        .map(m => [m.mappedTo, m.csvColumn])
    );

    const importation = <Importation>{
      type: ImportationTypeEnum.USER,
      filename: this.file?.name,
      contentType: this.file?.type,
      url: this.url,
      metadata: {
        totalSend: this.csvContent.length,
        totalExists: 0,
        totalInserted: 0,
        totalUpdated: 0,
        totalError: errorCount,
        mappings,
      },
    };

    this.loadingContent = true;
    this.importationService.create(importation)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.alertService.success(this.t('user.upload.importScheduledSuccess')),
        error: () => {
          this.alertService.error(this.t('user.upload.importScheduledError'));
          this.loadingContent = false;
        },
        complete: () => this.dialogRef.close(),
      });
  }

  formatBytes(bytes: number): string {
    const units = ['B', 'KB', 'MB', 'GB'];
    let index = 0;

    while (bytes >= 1024 && index < units.length - 1) {
      bytes /= 1024;
      index++;
    }

    return index === 0 ? `${bytes} ${units[index]}` : `${bytes.toFixed(2)} ${units[index]}`;
  }

  onMappingChange(changedIndex: number, newTarget: UserField): void {
    if (newTarget !== 'ignore') {
      this.mappings.forEach((m, i) => {
        if (i !== changedIndex && m.mappedTo === newTarget) {
          m.mappedTo = 'ignore';
        }
      });
    }
    this.mappings[changedIndex].mappedTo = newTarget;
  }

  isMappingValid(): boolean {
    return (['name', 'email', 'roles'] as UserField[]).every(field =>
      this.mappings.some(m => m.mappedTo === field),
    );
  }

  applyMappingAndBuildRows(): void {
    const mappingMap = new Map<UserField, string>(
      this.mappings.map(m => [m.mappedTo, m.csvColumn] as const),
    );

    const colName = mappingMap.get('name');
    const colEmail = mappingMap.get('email');
    const colPhone = mappingMap.get('phone');
    const colRoles = mappingMap.get('roles');

    const emailPattern = this.emailRegex;

    this.csvContent = this.rawRows
      .map((row: Record<string, any>, index: number) => {
        const cell = (col?: string) =>
          (col ? (row[col] ?? '') : '').toString().trim();

        const name = cell(colName);
        const email = cell(colEmail).toLowerCase();
        const phone = cell(colPhone).replace(/\D+/g, '');
        const roles = cell(colRoles);

        const isEmailValid = !!email && emailPattern.test(email);
        const isNameValid = !!name;

        const hasError = !isNameValid || !isEmailValid;

        return email
          ? { name, email, phone, roles, index, hasError }
          : null;
      })
      .filter((x): x is NonNullable<typeof x> => !!x);


    this.currentPage = 0;
    this.updatePaginatedContent();
  }

  updatePaginatedContent(): void {
    let data = this.csvContent;
    if (this.showOnlyErrors) {
      data = data.filter(row => row.hasError);
    }
    const start = this.currentPage * this.pageSize;
    this.paginatedContent = data.slice(start, start + this.pageSize);
  }

  delete(item: any) {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '500px',
    });
    dialogRef.afterClosed().subscribe(result => {
      if (Boolean(result) === true) {
        this.csvContent = this.csvContent.filter((i) => i.index !== item.index);
        this.updatePaginatedContent();
      }
    });
  }

  toggleShowOnlyErrors(): void {
    this.showOnlyErrors = !this.showOnlyErrors;
    this.currentPage = 0;
    this.updatePaginatedContent();
  }

  handlePage(event: any): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updatePaginatedContent();
  }

  getErrorCount(): number {
    return this.csvContent.filter(row => row.hasError).length;
  }

  removeAllErrors(): void {
    this.csvContent = this.csvContent.filter(row => !row.hasError);
    this.currentPage = 0;
    this.updatePaginatedContent();
  }

  onContinueFromUpload(): void {
    if (!this.canGoNext()) return;
    this.parseCsvAndStoreRaw();
    this.next();
  }

  onContinueFromMapping(): void {
    if (!this.canGoNext()) return;
    this.applyMappingAndBuildRows();
    this.next();
  }

  close() {
    this.dialogRef.close();
  }

  private buildDefaultMappings(): void {
    let headers = [...this.rawHeaders];
    let dataStart = 0;

    if (headers.every(h => /^\d+$/.test(h)) && this.rawRows.length) {
      const firstRow = this.rawRows[0];
      const firstValues = headers.map(h => String(firstRow[h] ?? '').trim());

      const looksLikeHeader = firstValues.some(v =>
        /(email|e-mail|telefone|celular|whats|nome|name|perfil|roles|permissao|role)/i.test(v),
      );

      if (looksLikeHeader) {
        headers = firstValues.map((v, i) => v || `col_${i + 1}`);
        dataStart = 1;
      }
    }

    this.mappings = headers.map((col, index) => {
      let sample = '';

      for (let r = dataStart; r < this.rawRows.length; r++) {
        const row = this.rawRows[r];
        const val = row[col] ?? row[this.rawHeaders[index]];
        if (val) { sample = String(val).trim(); break }
      }

      return {
        csvColumn: col,
        sample,
        mappedTo: this.inferField(col),
      };
    });
  }

  private inferField(columnName: string): UserField {
    const c = (columnName || '').toLowerCase().trim();

    if (/(telefone|celular|phone|whats|zap|numero|número)/.test(c)) return 'phone';
    if (/(nome|name|first\s*name|nome\s*completo|usuario|usuário)/.test(c)) return 'name';
    if (/(email|e-mail|e_mail|correio)/.test(c)) return 'email';
    if (/(perfil|roles|permissão|role|função|funcao|tipo)/.test(c)) return 'roles';

    return 'ignore';
  }

  private openModalAlert(title: string, message: string): void {
    const dialogRefAlert = this.dialog.open(AlertModalComponent, {
      width: '600px',
      maxHeight: '300px',
      data: { title, message },
    });
    dialogRefAlert.afterClosed().subscribe(() => this.inputFile.nativeElement.value = '');
  }
}
